import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { ERAS, BLEND_WIDTH } from '../config/eras'
import { resolveEraBlend } from '../core/time/timeEngine'
import { useExperienceStore } from '../store/experienceStore'

const vertexShader = /* glsl */ `
varying vec3 vWorldPosition;
void main() {
  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPosition.xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const fragmentShader = /* glsl */ `
precision highp float;
uniform sampler2D uTexA;
uniform sampler2D uTexB;
uniform float uBlend;
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform float uHasA;
uniform float uHasB;
uniform float uTime;
uniform float uIntensity;
varying vec3 vWorldPosition;

const float PI = 3.14159265359;

vec2 equirectUv(vec3 dir) {
  dir = normalize(dir);
  float u = atan(dir.z, dir.x) / (2.0 * PI) + 0.5;
  float v = asin(clamp(dir.y, -1.0, 1.0)) / PI + 0.5;
  return vec2(u, 1.0 - v);
}

void main() {
  vec3 dir = normalize(vWorldPosition);
  vec2 uv = equirectUv(dir);
  vec3 colA = uHasA > 0.5 ? texture2D(uTexA, uv).rgb : uColorA;
  vec3 colB = uHasB > 0.5 ? texture2D(uTexB, uv).rgb : uColorB;
  vec3 col = mix(colA, colB, clamp(uBlend, 0.0, 1.0));

  float shimmer = sin(uv.x * 40.0 + uTime * 2.0) * sin(uv.y * 20.0 - uTime) * 0.02 * (0.3 + uIntensity);
  col += shimmer;

  float vig = smoothstep(1.25, 0.3, length(uv - 0.5));
  col *= mix(0.82, 1.05, vig);

  // lift blacks so cinematic stills stay readable
  col = max(col, vec3(0.02));
  gl_FragColor = vec4(col, 1.0);
}
`

function hexToVec3(hex: string): THREE.Vector3 {
  const c = new THREE.Color(hex)
  return new THREE.Vector3(c.r, c.g, c.b)
}

const loader = new THREE.TextureLoader()
const textureCache = new Map<string, THREE.Texture>()

function loadEraTexture(url: string): Promise<THREE.Texture | null> {
  if (textureCache.has(url)) {
    return Promise.resolve(textureCache.get(url)!)
  }
  return new Promise((resolve) => {
    loader.load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace
        tex.wrapS = THREE.RepeatWrapping
        tex.wrapT = THREE.ClampToEdgeWrapping
        tex.minFilter = THREE.LinearMipmapLinearFilter
        tex.generateMipmaps = true
        textureCache.set(url, tex)
        resolve(tex)
      },
      undefined,
      () => resolve(null),
    )
  })
}

export function EnvironmentMorph() {
  const matRef = useRef<THREE.ShaderMaterial>(null)
  const markEraLoaded = useExperienceStore((s) => s.markEraLoaded)

  const uniforms = useMemo(
    () => ({
      uTexA: { value: null as THREE.Texture | null },
      uTexB: { value: null as THREE.Texture | null },
      uBlend: { value: 0 },
      uColorA: { value: new THREE.Vector3(0.05, 0.08, 0.15) },
      uColorB: { value: new THREE.Vector3(0.05, 0.08, 0.15) },
      uHasA: { value: 0 },
      uHasB: { value: 0 },
      uTime: { value: 0 },
      uIntensity: { value: 0 },
    }),
    [],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      for (const era of ERAS) {
        const url = `/assets/eras/${era.folder}/env.png`
        const tex = await loadEraTexture(url)
        if (cancelled) return
        if (tex) markEraLoaded(era.id)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [markEraLoaded])

  useFrame((state, dt) => {
    const { timeValue, traverseIntensity, tick } = useExperienceStore.getState()
    tick(dt)

    const blend = resolveEraBlend(timeValue, ERAS, BLEND_WIDTH)
    const mat = matRef.current
    if (!mat) return

    const urlA = `/assets/eras/${blend.from.folder}/env.png`
    const urlB = `/assets/eras/${blend.to.folder}/env.png`
    const texA = textureCache.get(urlA) ?? null
    const texB = textureCache.get(urlB) ?? null

    uniforms.uTexA.value = texA
    uniforms.uTexB.value = texB
    uniforms.uHasA.value = texA ? 1 : 0
    uniforms.uHasB.value = texB ? 1 : 0
    uniforms.uBlend.value = blend.from.id === blend.to.id ? 0 : blend.t
    uniforms.uColorA.value = hexToVec3(blend.from.colors[1])
    uniforms.uColorB.value = hexToVec3(blend.to.colors[1])
    uniforms.uTime.value = state.clock.elapsedTime
    uniforms.uIntensity.value = traverseIntensity
    mat.uniformsNeedUpdate = true
  })

  return (
    <mesh scale={[-1, 1, 1]} frustumCulled={false}>
      <sphereGeometry args={[50, 64, 40]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        side={THREE.BackSide}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  )
}
